import type { PageCMSError } from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import {
  type ApiBlock,
  BlockApiPropsMapSchema,
} from '@backmarket/http-api/src/api-specs-content/models/block'
import { get } from '@backmarket/utils/object/get'
import type { $ZodError, $ZodIssue } from 'zod/v4/core'

function hasMissingFieldError(issue: $ZodIssue) {
  return issue.code === 'invalid_type'
}

function createIssueMessage(issue: $ZodIssue) {
  return `${issue.message}: ${issue.path[issue.path.length - 1].toString()}`
}

function getIssueEntryId({
  block,
  issue,
}: {
  block: ApiBlock
  issue: $ZodIssue
}) {
  if (issue.path.length === 1) return null

  const pathToClosestParent = issue.path.slice(0, issue.path.length - 1)

  return get(block.props, pathToClosestParent)?.id || null
}

function formatErrors({ block, error }: { block: ApiBlock; error: $ZodError }) {
  return error.issues.reduce((acc, issue) => {
    // Early returns to only display missing field errors
    if (!hasMissingFieldError(issue)) return acc

    return [
      ...acc,
      {
        blockId: block.id,
        entryId: getIssueEntryId({ block, issue }),
        blockName: block.type,
        message: createIssueMessage(issue),
      },
    ]
  }, [] as PageCMSError[])
}

export function blockValidationErrors(block: ApiBlock): PageCMSError[] {
  const blockSchema = BlockApiPropsMapSchema[block.type]
  // if the block doesn't exist then there can't be any validation errors
  // if the block doesn't exist it also won't be rendered by BlockRenderer
  // so there's nothing to return
  if (!blockSchema) return []

  const { success, error } = blockSchema.safeParse(block.props)
  if (success) return []

  return formatErrors({ block, error })
}
